<template>
    <!-- *********************************************
    *                       Web                      *
    ********************************************** -->
    <web-info-page
    v-if="!getMobileAppState"
    :customBreadcrumbs="customBreadcrumbs"
    @backOrCloseClicked="emit('backOrCloseClicked')"/>
    <!-- *********************************************
    *                     Mobile                     *
    ********************************************** -->
    <wrapper-features-templates-operations
    v-else
    showFooter
    nonAuthenticatedFeature
    :customBreadcrumbs="customBreadcrumbs"
	:showOverlayLoader="showOverlayLoader"
	:activeStep="activeStep"
	:stepperParams="stepperParams"
    :buttonFullWidth="showInfoPage || activeStep === stepperParams.length + 1"
	:showStepper="activeStep <= stepperParams.length && !showInfoPage"
	:footerButtonsParams="footerButtonsParams"
    :scrollValue="scrollValue"
    @clearScrollToValue="scrollValue = null"
    @backOrCloseClicked="backCloseFeatureHandler">
        <!-- *********************************************
		*                      Content                   *
		********************************************** -->
		<component
		:is="getActiveStepComponent"
		:formData="formData"
		:clearFormErrors="clearFormErrors"
        :mandatoryList="mandatoryList[`step${activeStep}MandatoryList`]"
		:showMandatoryErrors="showMandatoryErrors"
        :processedDocumentsInfo="processedDocumentsInfo"
        :processId="processId"
        :documentValidated="documentValidated"
        :documentValidatedWithSuccess="documentValidatedWithSuccess"
        :companyNIFInfo="companyNIFInfo"
        :accessionNumber="accessionNumber"
        :showCountiesList="showCountiesList"
        :wsInfo="wsInfo"
		@scrollTo="scrollValue = $event"
        @validateDocument="validateDocumentHandler"
        @validateCompanyNIFHandler="validateCompanyNIFHandler"
        @requestCounties="requestCountiesHandler"
		@formErrorsCleared="clearFormErrors = false"
		@resetShowMandatoryErrorsProp="showMandatoryErrors = false"
		@updateFormData="updateFormDataHandler"
        @updateStep4FormData="updateStep4FormDataHandler"
		@updateFieldHasError="updateFieldHasError"
        @resendPassword="resendPasswordHandler"
        @applyLoader="showOverlayLoader = $event"/>
    </wrapper-features-templates-operations>
	<!-- Token Modal -->
	<modal-centered-token
    v-if="showTokenModal"
    :titleFull="t('join.createAccount.tokenModal.title')"
    :modalCustomUseParams="{
        messageToDisplay: t('join.createAccount.tokenModal.message'),
        sendTokenAction: 'joinCreateAccount/validateOTP',
        resendTokenAction: 'joinCreateAccount/resendValitationOTP',
        payload: { processId: processId, shouldValidateExistingProcesses: true },
        resendPayload: { processId: processId }
    }"
    @challengeSuccessHandler="validateOTPChallengeSuccessHandler"
    @challengeErrorHandler="[errorHandler($event, initCreateAccountProcess), showTokenModal = false]"/>
    <!-- Feedback Toasts -->
    <toasts-generic
	v-if="showFeedbackToast"
    :triggerClose="toastTriggerClose"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="[showFeedbackToast = false, toastTriggerClose = false]"/>
    <!-- Leave Feature Alert -->
    <modal-centered-generic
    v-if="showLeaveFeatureAlert"
    negative
    rightButtonIcon="check-line"
    :title="t('join.createAccount.leaveFeatureAlertRemoveTitle')"
    :text="t('join.createAccount.leaveFeatureAlertRemoveText')"
    :leftButtonText="t('join.createAccount.leaveFeatureAlertContinueBtn')"
    :rightButtonText="t('join.createAccount.leaveFeatureAlertLeaveBtn')"
    :triggerClose="triggerCloseLeaveFeatureAlert"
    @hideMe="[showLeaveFeatureAlert = false, triggerCloseLeaveFeatureAlert = false]"
    @rightButtonClicked="emit('backOrCloseClicked')"/>
    <!-- Leave Feature Alert -->
    <modal-centered-generic
    v-if="showContinueModal"
    :title="t('join.createAccount.continueModalTitle')"
    :text="t('join.createAccount.continueModalText')"
    :leftButtonText="t('join.createAccount.leftBtn')"
    :rightButtonText="t('join.createAccount.rightBtn')"
    :triggerClose="triggerCloseContinueModal"
    @hideMe="[showContinueModal = false, triggerCloseContinueModal = false]"
    @leftButtonClicked="startNewProcess"
    @rightButtonClicked="updateFormData"/>
</template>

<script setup>
import WebInfoPage from '@/components/features/nonAuthenticated/join/createAccount/WebInfoPage'
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import ModalCenteredToken from '@/components/generic/modals/centered/Token'
import ModalCenteredGeneric from '@/components/generic/modals/centered/Generic'
import ToastsGeneric from '@/components/generic/toasts/Generic'

/* Vue & Helpers */
import { ref, computed, defineAsyncComponent } from 'vue'
import { navigateByPath } from '@/router/helpers/navigation'
import { routeList } from '@/router/helpers/routes'
import { atLeastOneIsEmpty } from '@/assets/scripts/objects'
import { errorObjectConstructor, emptyObjectConstructor, sucessObjectConstrutor } from '@/assets/scripts/toastsHelpers'
import { createPayloadForMdw, updateFormDataWithPreSavedIndo } from '@/assets/scripts/parsers/createAccount'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

const {
    requestMaritalStatus,
    requestCountries,
    requestProvinces,
    requestCounties,
    requestLiteraryAbilities,
    requestProcessOccupation,
    requestProfessions,
    requestProfessionalActivities,
    requestContractualTies,
    requestAgenciesProvinces,
    requestBranches,
    startCreateAccountProcess,
    sendValitationOTP,
    getStepsListInfo,
    processDocumentsInfo,
    updateCreateAccountProcessInfo,
    validateDocument,
    uploadSupportingDocument,
    submitCreateAccountProcessInfo,
    resendPassword
 } = mapActions(moduleMapper.JOIN_CREATE_ACCOUNT)
const { getProcessSteps, getMaritalStatus, getCounties } = mapGetters(moduleMapper.JOIN_CREATE_ACCOUNT)
const { getMobileAppState } = mapGetters()

const customBreadcrumbs = {
    currentArea: t('join.createAccount.breadcrumbs.currentArea'),
    parentMenus: [{
        id: 'join',
        text: t('join.createAccount.breadcrumbs.firstParent')
    }]
}

/* Data */
const scrollValue = ref(null)
const showOverlayLoader = ref(false)
const showCountiesList = ref(false)
const showTokenModal = ref(false)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

/*************************************************
*                   Steps Logic                  *
*************************************************/
const showInfoPage = ref(true)
const activeStep = ref(1)
const showLeaveFeatureAlert = ref(false)
const triggerCloseLeaveFeatureAlert = ref(false)
const showContinueModal = ref(false)
const triggerCloseContinueModal = ref(false)

const goToStep = stepNumber => {
	activeStep.value = stepNumber
	scrollValue.value = 0
}

const stepperParams = ref([
	{ description: t('join.createAccount.stepper.step1') },
	{ description: t('join.createAccount.stepper.step2') },
	{ description: t('join.createAccount.stepper.step3') },
	{ description: t('join.createAccount.stepper.step4') },
	{ description: t('join.createAccount.stepper.step5') },
	{ description: t('join.createAccount.stepper.step6') },
	{ description: t('join.createAccount.stepper.step7') }
])

const getActiveStepComponent = computed(() => {
    if (showInfoPage.value) return defineAsyncComponent(() => import('@/components/features/nonAuthenticated/join/createAccount/MobileInfoPage'))

	const stepNumber = activeStep.value
	return defineAsyncComponent(() => import('@/components/features/nonAuthenticated/join/createAccount/Step' + stepNumber))
})

const fieldErrors = ref([])
const updateFieldHasError = ({ inputField, hasError }) => {
	if (hasError) {
		if (!fieldErrors.value.includes(inputField)) fieldErrors.value.push(inputField)
	} else {
		fieldErrors.value = fieldErrors.value.filter(string => string !== inputField)
	}
}

const validateMandatoryFormValues = () => {
	let hasEmptyFields = false
	if (mandatoryList.value[`step${activeStep.value}MandatoryList`]) {
		hasEmptyFields = atLeastOneIsEmpty(formData.value, mandatoryList.value[`step${activeStep.value}MandatoryList`])
	}

    if (activeStep.value === 3 && !processedDocumentsInfo.value?.hasSuccessfulIdDocumentValidation && !documentValidated.value) {
        if (atLeastOneIsEmpty(formData.value, step3PreValidateMandatoryList.value)) {
            showMandatoryErrors.value = true
            return
        }

        feedbackToastData.value = errorObjectConstructor({
            message: t('join.createAccount.step3.mandatoryValidateDocument'),
            dismissToastBtnText: t('commonButtons.gotIt')
        })
        showFeedbackToast.value = true
    }

    if (activeStep.value === 7 && !formData.value.conditions) {
        showMandatoryErrors.value = true
		return
    }

	if (hasEmptyFields || fieldErrors.value.length) {
		showMandatoryErrors.value = true
		return
	}

	switch (activeStep.value) {
		case 1:
			initCreateAccountProcess()
			break
        case 2:
            processDocuments()
            break
        case 3:
        case 4:
        case 6:
            updateProcessInfo()
            break
        case 7:
            submitFinishProcessInfo()
            break
		default:
			goToStep(activeStep.value + 1)
			break
	}
}

const backCloseFeatureHandler = () => {
    if (activeStep.value === 8 || activeStep.value < 2 || showInfoPage.value === true) {
        emit('backOrCloseClicked')
    } else showLeaveFeatureAlert.value = true
}

/*************************************************
*                    Form Logic                  *
*************************************************/
const processId = ref(null)
const forceNewProcessInsteadOfContinue = ref(false)
const processedDocumentsInfo = ref(null)
const documentValidated = ref(false)
const documentValidatedWithSuccess = ref(false)
const companyNIFInfo = ref(false)
const accessionNumber = ref(null)
const wsInfo = ref(null)

const formData = ref({
	phoneNumber: '',
    /* Document Data */
    frontFile: '',
    frontFileName: '',
    backFile: '',
    backFileName: '',
    /* Identification Data */
    idCardNumber: '',
    idCardEmissionDate: '',
    idCardExpirationDate: '',
    birthDate: '',
    /* Personal Data */
    nameOnIdCard: '',
    fatherName: '',
    motherName: '',
    maritalStatus: '',
    birthLocationProvince: '',
    gender: '',
    address: '',
    /* Step 4 General Info */
    generalInfo: null,
    email: '',
    numberOfChildren: '',
    houseHold: '',
    literaryAbilities: '',
    occupationalStatus: '',
    nifEmployerEntity: '',
    employerEntity: '',
    admissionDate: '',
    companyAddress: '',
    companyDoorNumber: '',
    companyFloorNumber: '',
    companyCountry: '',
    companyProvince: '',
    companyCounty: '',
    companyZipCode: '',
    occupationalStatusProof: null,
    profession: '',
    contractualBond: '',
    companyFunction: '',
    jobDescription: '',
    monthlyIncome: '',
    monthlyExpenses: '',
    corporateInterests: '',
    shareHoldingsList: [],
    /* Step 4 PPE */
    ppe: null,
    isPoliticalExposed: '',
    politicalPosition: '',
    hasFamilyPoliticalExposed: '',
    politicalFamilyName: '',
    politicalDegreeOfKinship: '',
    hasCommercialRelationhipWithPoliticalExposed: '',
    nameOfPoliticalExposed: '',
    /* Step 4 FATCA */
    fatca: null,
    hasLivedInTheUS: '',
    hasAmericanPassport: '',
    hasTaxObligationInTheUS: '',
    hasTin: '',
    tin: '',
    /* Step 5 Interview video */
    interviewVideo: null,
    /* Step 6 */
    branchId: '',
    /* Step 7 */
    conditions: false
})

const step1MandatoryList = ref([
    'phoneNumber'
])

const step2MandatoryList = ref([
    'frontFile',
    'backFile'
])

const step3PreValidateMandatoryList = ref([
    'idCardNumber',
    'idCardEmissionDate',
    'idCardExpirationDate',
    'birthDate'
])

const step3MandatoryList = ref([
    'idCardNumber',
    'idCardEmissionDate',
    'idCardExpirationDate',
    'birthDate',
    /* Personal Data */
    'nameOnIdCard',
    'fatherName',
    'motherName',
    'maritalStatus',
    'birthLocationProvince',
    'gender',
    'address'
])

/*
* Step 4 Subitens list,
* step makes the validations for subitens mandatory fields,
* since view only has the info after its been saved in subitem modal
*/
const step4MandatoryList = ref([
    'generalInfo',
    'ppe',
    'fatca'
])

/* Step 5 */
const step5MandatoryList = ref([
    'interviewVideo'
])

/* Step 6 */
const step6MandatoryList = ref([
    'branchId'
])

/* Step 7 */
const step7MandatoryList = ref([
    'conditions'
])

const mandatoryList = ref({
    step1MandatoryList: step1MandatoryList.value,
    step2MandatoryList: step2MandatoryList.value,
    step3MandatoryList: step3MandatoryList.value,
    step4MandatoryList: step4MandatoryList.value,
    step5MandatoryList: step5MandatoryList.value,
    step6MandatoryList: step6MandatoryList.value,
    step7MandatoryList: step7MandatoryList.value
})

const updateFormDataHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
    if (field === 'frontFile' || field === 'backFile') documentValidated.value = false
}

const updateStep4FormDataHandler = info => {
    formData.value = { ...formData.value, ...info }
}

/* Identification Data */
const updateFormDataDocumentOCRInfo = res => {
    if (res.idCardNumber) formData.value.idCardNumber = res.idCardNumber
    if (res.idCardEmissionDate) formData.value.idCardEmissionDate = res.idCardEmissionDate
    if (res.idCardExpirationDate) formData.value.idCardExpirationDate = res.idCardExpirationDate
    if (res.birthDate) formData.value.birthDate = res.birthDate
}

/* Personal Data */
const updateFormDataDocumentWSInfo = res => {
    wsInfo.value = res
    formData.value.nameOnIdCard = res.nameOnIdCard || ''
    formData.value.fatherName = res.fatherName || ''
    formData.value.motherName = res.motherName || ''
    formData.value.maritalStatus = getMaritalStatus.value.find(obj => obj.id === res.maritalStatus) ? res.maritalStatus : ''
    formData.value.birthLocationProvince = getCounties.value.find(obj => obj.id.toLowerCase() === res.birthLocationProvince.toLowerCase()) ? getCounties.value.find(obj => obj.id.toLowerCase() === res.birthLocationProvince.toLowerCase()).id : ''
    formData.value.gender = res.gender !== 'Unknown' ? res.gender : ''
    formData.value.address = res.address || ''
}

/*************************************************
*                  Requests Logic                *
*************************************************/
const initialRequests = async () => {
	showOverlayLoader.value = true

    /* We are using a Promise all to save sometime, calling all api's at the same time, since they dont depend on eachother */
    Promise.all([
        getStepsListInfo(),
        requestMaritalStatus(),
        requestCountries(),
        requestCounties(),
        requestProvinces(),
        requestLiteraryAbilities(),
        requestProcessOccupation(),
        requestProfessions(),
        requestProfessionalActivities(),
        requestContractualTies(),
        requestAgenciesProvinces(),
        requestBranches()
    ]).then(response => {
        showOverlayLoader.value = false
        showInfoPage.value = false
    }).catch((error) => {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
    })
}

const initProcessRes = ref(null)

const initCreateAccountProcess = async () => {
	showOverlayLoader.value = true

	const payload = {
		name: '',
		msisdn: formData.value.phoneNumber
	}

	if (forceNewProcessInsteadOfContinue.value) payload.processId = processId.value

	try {
		/*
		* If we have a processId we dont want to create another process,
		* unless the user expresses that he want to start a new process instead of continue the old one
		*/
		if (!processId.value || forceNewProcessInsteadOfContinue.value) {
			const res = await startCreateAccountProcess(payload)
			initProcessRes.value = res.data.data.customerCreationProcess
		}
		/*
		* If we are forcing the start of a new process, it means the user already validated the phoneNumber
		* for that reason, we will be skiping this logic
		*/
        processId.value = initProcessRes.value.processId
		if (!forceNewProcessInsteadOfContinue.value) {
			const sendTokenPayload = { processId: processId.value }
			await sendValitationOTP(sendTokenPayload)
			showTokenModal.value = true
		} else goToStep(2)
		showOverlayLoader.value = false
	} catch (error) {
        errorHandler(error.response.data, initCreateAccountProcess)
	}
}

const processDocuments = async () => {
	showOverlayLoader.value = true
    documentValidatedWithSuccess.value = false

    try {
        const res = await processDocumentsInfo({ processId: processId.value })
        processedDocumentsInfo.value = res.data.data
        showOverlayLoader.value = false
        if (processedDocumentsInfo.value?.hasSuccessfulOCR) {
            updateFormDataDocumentOCRInfo(processedDocumentsInfo.value.customerCreationProcess)
            documentValidatedWithSuccess.value = true
        }
        if (processedDocumentsInfo.value?.hasSuccessfulIdDocumentValidation) updateFormDataDocumentWSInfo(processedDocumentsInfo.value.customerCreationProcess)

        goToStep(3)
    } catch (error) {
        errorHandler(error.response.data, processDocuments)
    }
}

const validateDocumentCounter = ref(2)
const validateDocumentHandler = async () => {
    if (atLeastOneIsEmpty(formData.value, step3PreValidateMandatoryList.value)) {
        showMandatoryErrors.value = true
        return
    }

    showOverlayLoader.value = true
    toastTriggerClose.value = true

    const payload = {
        documentNumber: formData.value.idCardNumber,
        documentType: 'BI',
        processId: processId.value,
        issueDate: formData.value.idCardEmissionDate,
        expirationDate: formData.value.idCardExpirationDate,
        birthDate: formData.value.birthDate
    }

    try {
        const res = await validateDocument(payload)
        if (res.data.data.hasSuccessfulIdDocumentValidation) {
            updateFormDataDocumentOCRInfo(res.data.data.customerCreationProcess)
            updateFormDataDocumentWSInfo(res.data.data.customerCreationProcess)
            documentValidatedWithSuccess.value = true
        } else {
            if (validateDocumentCounter.value === 0) {
                feedbackToastData.value = errorObjectConstructor({
                    message: t('join.createAccount.step3.cantValidateDocument'),
                    dismissToastBtnText: t('commonButtons.gotIt')
                })
                showFeedbackToast.value = true
            } else {
                validateDocumentCounter.value = validateDocumentCounter.value - 1
                validateDocumentHandler()
                return
            }
        }
        documentValidated.value = true
        showOverlayLoader.value = false
    } catch (error) {
        if (error.response && error.response.data.code === 'invalid_id_card_data') {
            feedbackToastData.value = errorObjectConstructor({
                message: error.response.data.message,
                dismissToastBtnText: t('commonButtons.gotIt')
            })
            showFeedbackToast.value = true
            showOverlayLoader.value = false
        } else if (validateDocumentCounter.value === 0) {
            errorHandler(error.response.data, validateDocumentHandler)
        } else {
            validateDocumentCounter.value = validateDocumentCounter.value - 1
            validateDocumentHandler()
        }
    }
}

const validateCompanyNIFHandler = async $event => {
    companyNIFInfo.value = null
    showOverlayLoader.value = true

    try {
        const res = await validateDocument({ documentNumber: $event, documentType: 'NIF', processId: processId.value })
        if (res.data.data.hasSuccessfulIdDocumentValidation) {
            companyNIFInfo.value = { ...res.data.data.customerCreationProcess, hasSuccessfulIdDocumentValidation: res.data.data.hasSuccessfulIdDocumentValidation }
        }
        showOverlayLoader.value = false
    } catch (error) {
        companyNIFInfo.value = { hasSuccessfulIdDocumentValidation: false }
        errorHandler(error.response.data, validateDocumentHandler)
    }
}

const requestCountiesHandler = async $event => {
    showOverlayLoader.value = true
    showCountiesList.value = false

    try {
        await requestCounties({ provinceName: $event })
        showOverlayLoader.value = false
        showCountiesList.value = true
    } catch (error) {
        errorHandler(error.response.data, requestCountiesHandler)
    }
}

const updateProcessInfo = async () => {
	showOverlayLoader.value = true

    const payload = {
        ...createPayloadForMdw(activeStep.value, formData.value),
        customerCreationProcessStep: { stepType: getProcessSteps.value[activeStep.value - 1].stepType },
        processId: processId.value
    }

    delete payload.phoneNumber

    try {
        await updateCreateAccountProcessInfo(payload)
        /* If we are on step4 we need to also upload supporting docs */
        if (activeStep.value === 4 && formData.value.occupationalStatus && formData.value.occupationalStatus !== 4) {
            uploadSupportingDoc()
            return
        }
        /* removing loader and proceed to next step */
        showOverlayLoader.value = false
        goToStep(activeStep.value + 1)
    } catch (error) {
        errorHandler(error.response.data, updateProcessInfo)
    }
}

const buildFormData = ({ blob, fileName, fileType }) => {
    const form = new FormData()

    /* Blob Info */
    form.append('file', blob, `${fileName}.${fileType}`)

    return form
}

const uploadSupportingDoc = async () => {
	showOverlayLoader.value = true
    let payload

    if (formData.value.occupationalStatusProof.fileBlob) {
        payload = {
            params: { processId: processId.value },
            data: buildFormData({
                blob: formData.value.occupationalStatusProof.fileBlob,
                fileName: formData.value.occupationalStatusProof.fileName,
                fileType: formData.value.occupationalStatusProof.fileType
            })
        }
    }

    try {
        if (window.cordova) window.cordova.plugin.http.setDataSerializer('multipart')
        if (payload) await uploadSupportingDocument(payload)
        showOverlayLoader.value = false
        goToStep(5)
    } catch (error) {
        errorHandler(error.response.data, uploadSupportingDoc)
    } finally {
        if (window.cordova) window.cordova.plugin.http.setDataSerializer('json')
    }
}

const submitFinishProcessInfo = async () => {
    showOverlayLoader.value = true

    const payload = {
        data: { stepType: getProcessSteps.value[activeStep.value - 1].stepType },
        params: { processId: processId.value }
    }

    try {
        const res = await submitCreateAccountProcessInfo(payload)
        /* removing loader and proceed to next step */
        accessionNumber.value = res.data.data.username
        showOverlayLoader.value = false
        goToStep(activeStep.value + 1)
    } catch (error) {
        errorHandler(error.response.data, submitFinishProcessInfo)
    }
}

const resendPasswordHandler = async () => {
    showOverlayLoader.value = true

    try {
        const res = await resendPassword({ processId: processId.value })
        successHandler(res.data)
        showOverlayLoader.value = false
    } catch (error) {
        errorHandler(error.response.data, resendPasswordHandler)
    }
}
/*************************************************
*                   Footer Logic                 *
*************************************************/
/* Footer Buttons Data */
const footerButtonsData = computed(() => {
	return [{
		label: t('join.createAccount.footer.forward'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: !showInfoPage.value,
		callbackFunction: initialRequests
	}, {
		label: t('join.createAccount.footer.back'),
        icon: 'arrow-left-full',
		buttonPosition: 'left',
		hidden: showInfoPage.value || activeStep.value === 8,
		callbackFunction: () => { activeStep.value === 1 ? showInfoPage.value = true : goToStep(activeStep.value - 1) }
	}, {
		label: t('join.createAccount.footer.forward'),
		icon: 'arrow-right-full',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: showInfoPage.value || activeStep.value === 7 || activeStep.value === 8,
		callbackFunction: validateMandatoryFormValues
	}, {
		label: t('join.createAccount.footer.finish'),
		icon: 'check-line',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 7 || showInfoPage.value,
		callbackFunction: validateMandatoryFormValues
	}, {
		label: t('join.createAccount.footer.enter'),
		icon: 'login',
		buttonType: 'primary',
		iconPosition: 'right',
		hidden: activeStep.value !== 8 || showInfoPage.value,
		callbackFunction: () => { navigateByPath(routeList.HOME_MOBILE_PATH) }
	}]
})

const footerButtonsParams = computed(() => {
	return footerButtonsData.value.filter(ele => !ele.hidden)
})

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const toastTriggerClose = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())

const tempInfo = ref(null)
const validateOTPChallengeSuccessHandler = data => {
    showTokenModal.value = false
	showOverlayLoader.value = false
    tempInfo.value = null

    tempInfo.value = data.customerCreationProcessView

	/*
	* If user has a process started we will show them a modal asking if he wants to continue or start fresh
	*/
    if (data.customerCreationProcessView.hasOnGoingProcess) showContinueModal.value = true
    else goToStep(2)
}

const startNewProcess = () => {
    processId.value = tempInfo.value.processId
    forceNewProcessInsteadOfContinue.value = true
    initCreateAccountProcess()
}

const updateFormData = () => {
    processId.value = tempInfo.value.processId
    const lastFieldStepIndex = getProcessSteps.value.findIndex(obj => obj.stepType === tempInfo.value.customerCreationProcessStep.stepType)

    formData.value = { ...formData.value, ...updateFormDataWithPreSavedIndo(tempInfo.value, lastFieldStepIndex + 1) }
    triggerCloseContinueModal.value = true

    if (lastFieldStepIndex > 3) documentValidated.value = true
    goToStep(lastFieldStepIndex + 2)
}

const successHandler = data => {
    feedbackToastData.value = sucessObjectConstrutor(data.message)
	showFeedbackToast.value = true
}

const errorHandler = (data, retryFn, resolveBtnFunction) => {
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, resolveBtnFunction)
	showFeedbackToast.value = true
}
</script>

<style lang="scss" scoped>
</style>
